import React from "react";
import { useTranslation } from "react-i18next";
import "./styles/pages/SetupPage.css";
import { getBuildInfo } from "./version";
import { apiService } from "./services/api.service";
import { telemetryService } from "./services/telemetry.service";
import { GraphUser, GroupCreationSettings, FEATURE_KEYS } from "./types";
import { useFeature } from "./hooks";
import { ISV_AAD_APP_CLIENT_ID } from "./config/api.config";
import {
  Stack,
  Text,
  Toggle,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
} from "@fluentui/react";

/**
 * URL to Microsoft Entra ID group general settings blade
 */
const ENTRA_GROUP_SETTINGS_URL =
  "https://entra.microsoft.com/#view/Microsoft_AAD_IAM/GroupGeneralSettingsBlade?Microsoft_AAD_IAM_legacyAADRedirect=true";






interface Permission {
  name: string;
  description: string;
  id: string;
  granted: boolean;
  required: boolean;
  status: string;
  type: 'Role' | 'Scope';
}

interface PermissionsStatus {
  permissions: Permission[];
  summary: {
    totalRequired: number;
    requiredGranted: number;
    requiredMissing: number;
    extraPermissions: number;
    totalPermissions: number;
    allRequiredGranted: boolean;
  };
  tokenExpiresAt: string;
}

interface SetupPageProps {
  onConfigured?: () => void;
  onNavigateToAdmin?: () => void;
  onValidConfigurationChange?: (isValid: boolean) => void;
  theme?: string;
}

// ✨ MULTI-TENANT: Tenant onboarding status
interface TenantOnboardingStatus {
  checking: boolean;
  registering: boolean;
  exists: boolean;
  tenantId?: string;
  error?: string;
}

export default function SetupPage({ onConfigured, onNavigateToAdmin, onValidConfigurationChange, theme = "default" }: SetupPageProps) {
  const { t } = useTranslation(['common', 'setupPage']);

  const [loading, setLoading] = React.useState(true);

  const setupStartTime = React.useRef(Date.now());

  // ✨ MULTI-TENANT: Tenant onboarding state
  const [tenantStatus, setTenantStatus] = React.useState<TenantOnboardingStatus>({
    checking: true,
    registering: false,
    exists: false,
  });

  // 🔐 Service Principal detection state
  const [servicePrincipalMissing, setServicePrincipalMissing] = React.useState<boolean>(false);

  // 🚩 FEATURE FLAGS
  const isGroupLockFeatureEnabled = useFeature(FEATURE_KEYS.ENTRA_ID_GROUP_CREATION_LOCK);

  // 🔒 Teams Creation Lock / Allowed Security Group state
  const [groupCreationSettings, setGroupCreationSettings] = React.useState<GroupCreationSettings | null>(null);
  const [loadingSecurityGroup, setLoadingSecurityGroup] = React.useState<boolean>(false);

  // 📊 Telemetry preference state
  const [telemetryEnabled, setTelemetryEnabled] = React.useState<boolean>(true);
  const [loadingTelemetryPref, setLoadingTelemetryPref] = React.useState(false);







  const [permissionsStatus, setPermissionsStatus] = React.useState<PermissionsStatus | null>(null);
  const [loadingPermissions, setLoadingPermissions] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  // Progress tracking
  const [progressLogs, setProgressLogs] = React.useState<string[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [progressPercent, setProgressPercent] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState("");

  // Settings initialization state
  const [settingsInitialized, setSettingsInitialized] = React.useState(false);
  const [checkingSettings, setCheckingSettings] = React.useState(false);


  // 🔐 Monitor configuration validity and notify parent component
  React.useEffect(() => {
    const hasValidConfiguration = !servicePrincipalMissing && permissionsStatus?.summary.allRequiredGranted === true;

    console.log("[SetupPage] 🔐 Configuration status changed:", {
      servicePrincipalMissing,
      allRequiredGranted: permissionsStatus?.summary.allRequiredGranted,
      hasValidConfiguration
    });

    if (onValidConfigurationChange) {
      onValidConfigurationChange(hasValidConfiguration);
    }
  }, [servicePrincipalMissing, permissionsStatus, onValidConfigurationChange]);


  // ✨ MULTI-TENANT: Check and register tenant on mount (FIRST)
  React.useEffect(() => {
    checkAndRegisterTenant();
  }, []);

  // Load SharePoint status and permissions AFTER tenant is registered
  React.useEffect(() => {
    const loadData = async () => {
      if (!tenantStatus.checking && !tenantStatus.registering) {
        setLoading(true);
        await loadPermissionsStatus();
        setLoading(false);
      }
    };
    loadData();
  }, [tenantStatus.checking, tenantStatus.registering]);



  /**
   * ✨ MULTI-TENANT: Check if tenant exists and register if needed
   * This runs FIRST before any SharePoint setup
   * 🔒 RACE CONDITION FIX: Handle "Tenant already exists" error gracefully
   */
  const checkAndRegisterTenant = async () => {
    try {
      setTenantStatus({ checking: true, registering: false, exists: false });
      addProgressLog("🔍 Vérification du tenant...");

      // 1. Get Teams context to extract Azure Tenant ID
      let teamsJs: typeof import("@microsoft/teams-js") | null = null;
      let azureTenantId: string | undefined;
      let currentUser: { id?: string; email?: string } | undefined;

      try {
        teamsJs = await import("@microsoft/teams-js");
        await teamsJs.app.initialize();

        const context = await teamsJs.app.getContext();
        azureTenantId = context.user?.tenant?.id;
        currentUser = {
          id: context.user?.id,
          email: context.user?.userPrincipalName,
        };

        addProgressLog(`✅ Azure Tenant ID récupéré: ${azureTenantId}`);
      } catch (error) {
        console.error("Error getting Teams context:", error);
        addProgressLog("⚠️ Impossible de récupérer le contexte Teams");
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: "Impossible de récupérer le contexte Teams",
        });
        return;
      }

      if (!azureTenantId) {
        addProgressLog("⚠️ Azure Tenant ID introuvable dans le contexte Teams");
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: "Azure Tenant ID introuvable",
        });
        return;
      }

      // 2. Check if tenant exists
      addProgressLog("🔍 Vérification de l'existence du tenant...");
      const checkStartTime = Date.now();
      const checkData = await apiService.checkTenant(azureTenantId);

      if (checkData.exists && checkData.tenant) {
        // Tenant exists
        addProgressLog(`✅ Tenant existant trouvé: ${checkData.tenant.tenantName}`);
        telemetryService.capture('setup_tenant_check_completed', currentUser?.id || 'unknown', {
          properties: {
            tenant_exists: true,
            tenant_id: checkData.tenant.tenantId,
            azure_tenant_id: azureTenantId,
            duration_ms: Date.now() - checkStartTime,
          }
        });
        setTenantStatus({
          checking: false,
          registering: false,
          exists: true,
          tenantId: checkData.tenant.tenantId,
        });
        return;
      }

      // 3. Tenant doesn't exist - register automatically
      addProgressLog("🚀 Enregistrement automatique du tenant...");
      setTenantStatus({ checking: false, registering: true, exists: false });

      // ✅ CROSS-TENANT: Use apiService to include user token for OBO flow
      const registerData = await apiService.registerTenant({
        azureTenantId,
        createdBy: currentUser?.id,
        createdByEmail: currentUser?.email,
      });

      if (registerData.success) {
        // ✅ FIX: Access tenant object from the correct response structure
        // The backend returns tenant directly in the response, not nested under data
        const response = registerData as any;
        const tenant = response.tenant || response.data;
        addProgressLog(`✅ Tenant enregistré avec succès: ${tenant?.tenantName || 'Inconnu'}`);
        addProgressLog(`🏷️ Subdomain: ${tenant?.subdomain || 'N/A'}`);
        telemetryService.capture('setup_tenant_registered', currentUser?.id || 'unknown', {
          properties: {
            azure_tenant_id: azureTenantId,
            tenant_name: tenant?.tenantName || '',
            subdomain: tenant?.subdomain || '',
            tenant_id: tenant?.tenantId || tenant?.azureTenantId || '',
          }
        });
        setTenantStatus({
          checking: false,
          registering: false,
          exists: true,
          tenantId: tenant?.tenantId || tenant?.azureTenantId,
        });
      } else {
        // 🔒 RACE CONDITION FIX: Handle "Tenant already exists" error
        // This can happen if another user/tab registered the tenant between check and register
        const errorMessage = registerData.error || t('common:setup.tenantRegistrationError', 'Error registering tenant');
        
        const isRaceCondition = errorMessage.includes("already exists") || errorMessage.includes("Tenant already exists");
        telemetryService.capture('setup_tenant_registration_failed', currentUser?.id || 'unknown', {
          properties: {
            error_message: errorMessage,
            azure_tenant_id: azureTenantId,
            race_condition: isRaceCondition,
          }
        });
        
        if (isRaceCondition) {
          console.warn("[SetupPage] 🔄 Race condition detected: Tenant was registered by another request. Retrying check...");
          addProgressLog("🔄 " + t('common:setup.raceConditionDetected', 'Race condition detected: Tenant was registered by another request. Checking...'));
          
          // Retry the check to get the newly registered tenant info
          try {
            const retryCheckData = await apiService.checkTenant(azureTenantId);
            
            if (retryCheckData.exists && retryCheckData.tenant) {
              addProgressLog(`✅ ${t('common:setup.tenantFoundAfterConcurrentRegistration', 'Tenant found after concurrent registration')}: ${retryCheckData.tenant.tenantName}`);
              setTenantStatus({
                checking: false,
                registering: false,
                exists: true,
                tenantId: retryCheckData.tenant.tenantId,
              });
              return;
            }
          } catch (retryError) {
            console.error("[SetupPage] ❌ Failed to retry tenant check:", retryError);
          }
        }
        
        addProgressLog(`❌ ${t('common:setup.tenantRegistrationError', 'Error registering tenant')}: ${errorMessage}`);
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: errorMessage,
        });
      }
    } catch (error) {
      console.error("Error in checkAndRegisterTenant:", error);
      addProgressLog(`❌ ${t('common:setup.unknownError', 'Unknown error')}: ${error instanceof Error ? error.message : t('common:setup.unknownError', 'Unknown error')}`);
      setTenantStatus({
        checking: false,
        registering: false,
        exists: false,
        error: error instanceof Error ? error.message : t('common:setup.unknownError', 'Unknown error'),
      });
    }
  };



  const checkSettingsInitialization = async () => {
    try {
      setCheckingSettings(true);
      const data = await apiService.getSettings();

      // Settings are initialized if:
      // 1. Request succeeds
      // 2. Data is not null (settings exist on current site)
      // 3. At least one approver exists
      if (data.success && data.data && data.data.approvers && data.data.approvers.length > 0) {
        setSettingsInitialized(true);
      } else {
        // If request fails, returns null, or no approvers, settings are not initialized
        setSettingsInitialized(false);
      }
    } catch (error) {
      console.error("Error checking settings initialization:", error);
      setSettingsInitialized(false);
    } finally {
      setCheckingSettings(false);
    }
  };

  const loadPermissionsStatus = async (): Promise<PermissionsStatus | null> => {
    try {
      setLoadingPermissions(true);
      const data = await apiService.getPermissionsStatus();

      if (data.success) {
        setPermissionsStatus(data.data);

        telemetryService.capture('setup_permissions_loaded', 'setup-page', {
          properties: {
            total_required: data.data.summary.totalRequired,
            required_granted: data.data.summary.requiredGranted,
            required_missing: data.data.summary.requiredMissing,
            extra_permissions: data.data.summary.extraPermissions,
            all_required_granted: data.data.summary.allRequiredGranted,
          }
        });

        // ✨ Si toutes les permissions requises sont accordées, mettre à jour les infos du tenant
        if (data.data.summary.allRequiredGranted) {
          console.log("[SetupPage] ✅ All required permissions granted - refreshing tenant info...");

          try {
            // Obtenir le tenant ID depuis Teams context
            const teamsJs = await import("@microsoft/teams-js");
            try { await teamsJs.app.initialize(); } catch {}
            const context = await teamsJs.app.getContext();
            const clientTenantId = context.user?.tenant?.id;

            if (!clientTenantId) {
              console.error("[SetupPage] ❌ Cannot refresh tenant info: No client tenant ID in Teams context");
              return data.data;
            }

            // Récupérer l'état actuel du tenant pour comparaison
            const currentTenantResponse = await apiService.getTenantInfo(clientTenantId);
            const currentTenant = currentTenantResponse.success ? currentTenantResponse.data : null;

            console.log("[SetupPage] 🔄 Calling refreshTenantInfo for tenant:", clientTenantId);

            const refreshResult = await apiService.refreshTenantInfo(clientTenantId);

            console.log("[SetupPage] 📊 Refresh result:", refreshResult);

            if (refreshResult.success) {
              const newTenant = refreshResult.data;

              // Comparer les anciennes et nouvelles valeurs
              const tenantNameChanged = currentTenant?.tenantName !== newTenant?.tenantName;
              const subdomainChanged = currentTenant?.subdomain !== newTenant?.subdomain;

              if (tenantNameChanged || subdomainChanged) {
                console.log("[SetupPage] ✅ Tenant info updated successfully:", {
                  tenantName: newTenant?.tenantName,
                  subdomain: newTenant?.subdomain,
                });
              } else {
                console.log("[SetupPage] 🧘 No changes detected in tenant info. No alert shown.");
              }
            } else {
              console.error("[SetupPage] ❌ Failed to refresh tenant info:", {
                error: refreshResult.error,
                message: refreshResult.message,
                details: refreshResult.data?.details
              });

              // Afficher l'erreur à l'utilisateur
              alert(t('setupPage:alerts.tenantUpdateError', {
                error: refreshResult.message || refreshResult.error
              }));
            }
          } catch (refreshError: any) {
            console.error("[SetupPage] ❌ Exception during tenant info refresh:", {
              error: refreshError.message,
              stack: refreshError.stack
            });

         alert(t('setupPage:alerts.tenantUpdateException', {
              details: refreshError.message
            }));
          }
        }
        return data.data;
      }
      return null;
    } catch (error) {
      console.error("[SetupPage] Error loading permissions status:", error);
      return null;
    } finally {
      setLoadingPermissions(false);
    }
  };

  const refreshPermissions = async () => {
    try {
      console.log("[SetupPage] Refreshing Graph token and permissions...");
      setLoadingPermissions(true);

      telemetryService.capture('setup_permissions_refresh_clicked', 'setup-page', { properties: {} });

      // Force token refresh by resetting Graph client
      const refreshData = await apiService.refreshToken();

      console.log("[SetupPage] Token refresh response:", refreshData);

      // Reload permissions status with the new token
      const latestPermissions = await loadPermissionsStatus();

      // If all permissions are granted → show success modal
      if (latestPermissions?.summary.allRequiredGranted) {
        console.log("[SetupPage] ✅ All permissions granted - showing success modal");
        telemetryService.capture('setup_completed_after_permissions', 'setup-page', {
          properties: {
            duration_ms: Date.now() - setupStartTime.current,
            permissions_all_granted: true,
            creation_lock_active: groupCreationSettings?.enableGroupCreation === false,
          }
        });
        setShowSuccessModal(true);
        return;
      }

      console.log("[SetupPage] Permissions refreshed successfully");
    } catch (error) {
      console.error("[SetupPage] Error refreshing permissions:", error);
      setLoadingPermissions(false);
    }
  };

  const handleGrantAdminConsent = async () => {
    if (!ISV_AAD_APP_CLIENT_ID) {
      console.error('Missing ISV_AAD_APP_CLIENT_ID in environment variables');
      alert(t('setupPage:permissions.configMissing'));
      return;
    }

    // Obtenir le tenant ID CLIENT depuis Teams context
    let clientTenantId: string | undefined;
    try {
      const teamsJs = await import("@microsoft/teams-js");
      try { await teamsJs.app.initialize(); } catch {}
      const context = await teamsJs.app.getContext();
      clientTenantId = context.user?.tenant?.id;
    } catch (error) {
      console.error('Could not get client tenant ID from Teams context:', error);
    }

    if (!clientTenantId) {
      alert(t('setupPage:permissions.tenantIdError'));
      return;
    }

    // Consentement dans le tenant CLIENT (pas le tenant ISV)
    const consentUrl = `https://login.microsoftonline.com/${clientTenantId}/adminconsent?client_id=${ISV_AAD_APP_CLIENT_ID}`;
    console.log('[AdminConsent] Opening consent URL for CLIENT tenant:', { clientTenantId, consentUrl });
    window.open(consentUrl, '_blank');
  };

  // 🔒 Load the allowed security group / Entra ID creation lock status
  const loadAllowedSecurityGroup = async () => {
    if (!isGroupLockFeatureEnabled) {
      setGroupCreationSettings(null);
      return;
    }
    setLoadingSecurityGroup(true);
    try {
      const resp = await apiService.getAllowedSecurityGroup();
      console.log("[SetupPage] loadAllowedSecurityGroup response:", resp);
      if (resp.success && resp.data) {
        console.log("[SetupPage] Setting groupCreationSettings to:", resp.data);
        setGroupCreationSettings(resp.data);
      } else {
        setGroupCreationSettings(null);
      }
    } catch (e) {
      console.error("[SetupPage] Error loading allowed security group:", e);
      setGroupCreationSettings(null);
    } finally {
      setLoadingSecurityGroup(false);
    }
  };

  // Load security group on mount or when feature is enabled
  React.useEffect(() => {
    if (!tenantStatus.checking && !tenantStatus.registering && isGroupLockFeatureEnabled) {
      loadAllowedSecurityGroup();
    }
  }, [tenantStatus.checking, tenantStatus.registering, isGroupLockFeatureEnabled]);

  // Load telemetry preference on mount
  const loadTelemetryPreference = async () => {
    try {
      setLoadingTelemetryPref(true);
      const data = await apiService.getTelemetryPreferences();

      if (data.success && data.data !== undefined) {
        setTelemetryEnabled(data.data.telemetryEnabled ?? true); // Default to true if undefined
      } else {
        // Default to true if no preference is set
        setTelemetryEnabled(true);
      }
    } catch (error) {
      console.error("Error loading telemetry preference:", error);
      // Default to true on error
      setTelemetryEnabled(true);
    } finally {
      setLoadingTelemetryPref(false);
    }
  };

  React.useEffect(() => {
    if (!tenantStatus.checking && !tenantStatus.registering) {
      loadTelemetryPreference();
    }
  }, [tenantStatus.checking, tenantStatus.registering]);


  const addProgressLog = (message: string) => {
    setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString('fr-FR')} - ${message}`]);
  };





  if (loading || tenantStatus.checking) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-loading">
          <div className="spinner"></div>
          <p>
            {tenantStatus.checking
              ? t('setupPage:loading.tenantChecking')
              : t('setupPage:loading.configurationChecking')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`setup-page theme-${theme}`}>
      <div className="setup-header">
        <h1>{t('setupPage:header.title')}</h1>
        <p>{t('setupPage:header.subtitle')}</p>
      </div>

      {/* ✨ MULTI-TENANT: Tenant Onboarding Status */}
      {(tenantStatus.checking || tenantStatus.registering || tenantStatus.error) && (
        <div className="setup-section">
          <h2>{t('setupPage:tenantOnboarding.title')}</h2>
          <div className="status-card">
            {tenantStatus.checking && (
              <MessageBar messageBarType={MessageBarType.info}>
                {t('setupPage:tenantOnboarding.checking')}
              </MessageBar>
            )}
            {tenantStatus.registering && (
              <MessageBar messageBarType={MessageBarType.info}>
                {t('setupPage:tenantOnboarding.registering')}
              </MessageBar>
            )}
            {tenantStatus.error && (
              <MessageBar messageBarType={MessageBarType.error}>
                {t('setupPage:tenantOnboarding.error', { error: tenantStatus.error })}
              </MessageBar>
            )}
            {tenantStatus.exists && tenantStatus.tenantId && (
              <MessageBar messageBarType={MessageBarType.success}>
                {t('setupPage:tenantOnboarding.success', { tenantId: tenantStatus.tenantId })}
              </MessageBar>
            )}
          </div>
        </div>
      )}

      {/* Progress Section - Sticky at top */}
      {isProcessing && (
        <div className="progress-section">
          <MessageBar
            messageBarType={MessageBarType.info}
            onDismiss={() => setIsProcessing(false)}
            dismissButtonAriaLabel={t('setupPage:alerts.close')}
          >
            {progressMessage}
          </MessageBar>
          <ProgressIndicator
            label={t('setupPage:progress.label')}
            description={progressMessage}
            percentComplete={progressPercent}
          />
          {progressLogs.length > 0 && (
            <div className="progress-logs">
              <Stack tokens={{ childrenGap: 4 }}>
                {progressLogs.map((log, index) => (
                  <Text key={index} variant="small" block styles={{ root: { fontFamily: 'monospace', padding: '4px 8px', background: '#f3f2f1', borderRadius: '4px' } }}>
                    {log}
                  </Text>
                ))}
              </Stack>
            </div>
          )}
        </div>
      )}

      {/* Status Section */}
      <div className="setup-section">
        {servicePrincipalMissing ? (
          <>
            <div style={{
              padding: "40px",
              background: "#ffffff",
              border: "3px solid #6264a7",
              borderRadius: "16px",
              marginTop: "30px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ fontSize: "64px", marginBottom: "20px" }}>🔐</div>
              <h2 style={{ color: "#323130", fontSize: "28px", marginTop: 0, marginBottom: "20px", fontWeight: "600" }}>
                {t('setupPage:servicePrincipal.title')}
              </h2>
              <p style={{ fontSize: "18px", marginBottom: "30px", lineHeight: "1.6", color: "#323130" }}>
                {t('setupPage:servicePrincipal.description')}
              </p>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (!ISV_AAD_APP_CLIENT_ID) {
                    alert(t('setupPage:servicePrincipal.configMissing'));
                    return;
                  }

                  let clientTenantId: string | undefined;
                  try {
                    const teamsJs = await import("@microsoft/teams-js");
                    try { await teamsJs.app.initialize(); } catch {}
                    const context = await teamsJs.app.getContext();
                    clientTenantId = context.user?.tenant?.id;
                  } catch (error) {
                    console.error('Could not get client tenant ID:', error);
                  }

                  if (!clientTenantId) {
                    alert(t('setupPage:servicePrincipal.tenantIdError'));
                    return;
                  }

                  const consentUrl = `https://login.microsoftonline.com/${clientTenantId}/adminconsent?client_id=${ISV_AAD_APP_CLIENT_ID}`;
                  console.log('[AdminConsent] Opening:', consentUrl);
                  telemetryService.capture('setup_admin_consent_initiated', 'setup-page', {
                    properties: {
                      client_tenant_id: clientTenantId,
                      client_id: ISV_AAD_APP_CLIENT_ID,
                    }
                  });
                  window.open(consentUrl, '_blank');
                }}
                style={{ fontSize: "20px", padding: "16px 40px", marginBottom: "25px", fontWeight: "600" }}
              >
                {t('setupPage:servicePrincipal.createButton')}
              </button>

              <div style={{
                background: "#f3f2f1",
                padding: "24px",
                borderRadius: "8px",
                marginTop: "30px",
                textAlign: "left",
                border: "1px solid #e1dfdd"
              }}>
                <h3 style={{ marginTop: 0, color: "#6264a7", fontSize: "18px", fontWeight: "600" }}>{t('setupPage:servicePrincipal.instructions.title')}</h3>
                <ol style={{ marginLeft: "20px", lineHeight: "2", color: "#323130", fontSize: "15px" }}>
                  <li>{t('setupPage:servicePrincipal.instructions.step1')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step2')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step3')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step4')}</li>
                </ol>
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
                style={{ marginTop: "25px", fontSize: "16px", fontWeight: "500" }}
              >
                {t('setupPage:servicePrincipal.refreshButton')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>{t('setupPage:configStatus.title')}</h2>

            <div className="status-grid">
              {/* Permissions Microsoft Graph */}
              <div className="status-card">
                <div className="status-header">
                  <span className="status-icon">
                    {servicePrincipalMissing ? "🚫" : loadingPermissions ? "⏳" : permissionsStatus?.summary.allRequiredGranted ? "✅" : "⚠️"}
                  </span>
                  <h3>{t('setupPage:permissions.title')}</h3>
                </div>
                <div className="status-content">
                  {loadingPermissions ? (
                    <div className="loading-spinner">
                      <div className="spinner-small"></div>
                      <span>{t('setupPage:permissions.checking')}</span>
                    </div>
                  ) : permissionsStatus ? (
                    <>
                      <div className="tables-list">
                        {permissionsStatus.permissions.filter(p => p.required).map((permission, index) => (
                          <div key={permission.id || index} className={`table-item ${permission.granted ? "exists" : "missing"}`}>
                            <span className="table-status">{permission.granted ? "✅" : "❌"}</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                              <span className="table-name">{permission.name}</span>
                              <span style={{ fontSize: "11px", color: "var(--color-text-tertiary)", fontStyle: "italic" }}>
                                {permission.type === "Role" ? t('setupPage:permissions.roleType') : t('setupPage:permissions.scopeType')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {permissionsStatus.summary.allRequiredGranted ? (
                        <div>
                          <p className="status-ok">
                            {t('setupPage:permissions.allGranted')}
                            {permissionsStatus.summary.extraPermissions > 0 && (
                              <> {t('setupPage:permissions.extraCount', { count: permissionsStatus.summary.extraPermissions })}</>
                            )}
                          </p>
                          {permissionsStatus.summary.extraPermissions > 0 && (
                            <p style={{ fontSize: "12px", color: "var(--color-text-secondary)", marginTop: "4px", marginBottom: "8px" }}>
                              {t('setupPage:permissions.extraPermissionsNotice')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="status-warning">
                          {t('setupPage:permissions.missingCount', { count: permissionsStatus.summary.requiredMissing })}
                        </p>
                      )}

                      {/* Bouton de consentement pour accorder les permissions manquantes OU mettre à jour l'app si des permissions sont en trop */}
                      {(!permissionsStatus.summary.allRequiredGranted || permissionsStatus.summary.extraPermissions > 0) && (
                        <button
                          className="btn btn-primary btn-small"
                          onClick={handleGrantAdminConsent}
                          style={{ marginTop: "10px", marginRight: "10px" }}
                        >
                          {permissionsStatus.summary.allRequiredGranted
                            ? t('setupPage:permissions.updateConsentButton')
                            : t('setupPage:permissions.grantButton')}
                        </button>
                      )}

                      <button
                        className="btn btn-secondary btn-small"
                        onClick={refreshPermissions}
                        style={{ marginTop: "10px" }}
                      >
                        {t('setupPage:permissions.refreshButton')}
                      </button>

                      {permissionsStatus.permissions.filter(p => !p.required).length > 0 && (
                        <details style={{ marginTop: "10px" }}>
                          <summary style={{ cursor: 'pointer', color: 'var(--color-brand-primary)', fontSize: '14px' }}>
                            {t('setupPage:permissions.showExtra')}
                          </summary>
                          <div className="tables-list" style={{ marginTop: "8px" }}>
                            {permissionsStatus.permissions.filter(p => !p.required).map((permission, index) => (
                              <div key={permission.id || index} className="table-item exists">
                                <span className="table-status">✅</span>
                                <span className="table-name">{permission.name}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  ) : (
                    <p className="status-warning">{t('setupPage:permissions.loadError')}</p>
                  )}
                </div>
              </div>
            </div>


            {/* 🔒 Teams Creation Lock Section */}
            {isGroupLockFeatureEnabled && (
            <div className="setup-section">
              <h2>{t('setupPage:securityGroup.title')}</h2>
              <p className="help-text">
                {t('setupPage:securityGroup.description')}
              </p>

              <div className="status-card">
                <div className="status-content">
                  <div className="admin-status-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3 className="admin-section-title">{t('setupPage:securityGroup.label')}</h3>
                    <button
                      onClick={loadAllowedSecurityGroup}
                      disabled={loadingSecurityGroup}
                      className="btn btn-secondary"
                      style={{ padding: "6px 12px", fontSize: "13px" }}
                    >
                      {loadingSecurityGroup ? t('common:app.loading.general') : t('setupPage:securityGroup.refreshButton')}
                    </button>
                  </div>

                  {/* Cas 1 : Verrouillage actif avec groupe spécifique autorisé */}
                  {groupCreationSettings && groupCreationSettings.enableGroupCreation === false && (groupCreationSettings.allowedSecurityGroup || groupCreationSettings.displayName) ? (
                    <div className="setup-lock-card setup-lock-active">
                      <div className="setup-lock-header">
                        <span className="setup-lock-icon">🔒</span>
                        <strong className="setup-lock-title">
                          {t('setupPage:securityGroup.status.lockedWithGroup')}
                        </strong>
                      </div>
                      <p className="setup-lock-description">
                        {t('setupPage:securityGroup.status.lockedWithGroupDescription')}
                      </p>

                      <div className="setup-lock-group-box">
                        <div className="setup-lock-group-title">
                          {t('setupPage:securityGroup.groupDetails.title')}
                        </div>
                        <div className="setup-lock-grid">
                          <span className="setup-lock-label">{t('setupPage:securityGroup.groupDetails.name')}</span>
                          <span className="setup-lock-value">{groupCreationSettings.allowedSecurityGroup?.displayName || groupCreationSettings.displayName || "-"}</span>

                          {(groupCreationSettings.allowedSecurityGroup?.id || groupCreationSettings.id) && (
                            <>
                              <span className="setup-lock-label">{t('setupPage:securityGroup.groupDetails.id')}</span>
                              <code className="setup-lock-code">
                                {groupCreationSettings.allowedSecurityGroup?.id || groupCreationSettings.id}
                              </code>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : groupCreationSettings && groupCreationSettings.enableGroupCreation === false ? (
                    /* Cas 2 : Verrouillage actif pour tous (aucun groupe d'exception) */
                    <div className="setup-lock-card setup-lock-active">
                      <div className="setup-lock-header">
                        <span className="setup-lock-icon">🔒</span>
                        <strong className="setup-lock-title">
                          {t('setupPage:securityGroup.status.lockedAll')}
                        </strong>
                      </div>
                      <p className="setup-lock-description" style={{ marginBottom: 0 }}>
                        {t('setupPage:securityGroup.status.lockedAllDescription')}
                      </p>
                    </div>
                  ) : (
                    /* Cas 3 : Non verrouillé / Création ouverte à tous */
                    <div className="setup-lock-card setup-lock-warning">
                      <div className="setup-lock-header">
                        <span className="setup-lock-icon">⚠️</span>
                        <strong className="setup-lock-title">
                          {t('setupPage:securityGroup.status.unlockedWarningTitle')}
                        </strong>
                      </div>
                      <p className="setup-lock-description">
                        {t('setupPage:securityGroup.status.unlockedWarningMessage')}
                      </p>

                      <div className="setup-lock-steps-box">
                        <strong className="setup-lock-steps-title">
                          {t('setupPage:securityGroup.status.unlockedStepsTitle')}
                        </strong>
                        <ol className="setup-lock-steps-list">
                          <li>{t('setupPage:securityGroup.status.unlockedStep1')}</li>
                          <li>{t('setupPage:securityGroup.status.unlockedStep2')}</li>
                          <li>{t('setupPage:securityGroup.status.unlockedStep3')}</li>
                        </ol>
                        <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-text-secondary)', fontStyle: 'italic', borderTop: '1px dashed var(--color-border-primary)', paddingTop: '6px' }}>
                          {t('setupPage:securityGroup.status.unlockedPrivilegeNote')}
                        </div>
                      </div>

                      <a
                        href={ENTRA_GROUP_SETTINGS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                        style={{ display: "inline-block" }}
                      >
                        🔗 {t('setupPage:securityGroup.entraAdminLink')}
                      </a>
                    </div>
                  )}

                  {/* Impact details */}
                  <details className="setup-lock-details">
                    <summary className="setup-lock-summary">
                      ℹ️ {t('setupPage:securityGroup.impact.title')}
                    </summary>
                    <ul className="setup-lock-impact-list">
                      <li>{t('setupPage:securityGroup.impact.item1')}</li>
                      <li>{t('setupPage:securityGroup.impact.item2')}</li>
                      <li>{t('setupPage:securityGroup.impact.item3')}</li>
                      <li>{t('setupPage:securityGroup.impact.item4')}</li>
                      <li>{t('setupPage:securityGroup.impact.item5')}</li>
                      <li>{t('setupPage:securityGroup.impact.item6')}</li>
                      <li>{t('setupPage:securityGroup.impact.item7')}</li>
                    </ul>
                  </details>
                </div>
              </div>
            </div>
            )}

            {/* 📊 Telemetry Preferences Section */}
            <div className="setup-section">
              <h2>{t('setupPage:telemetry.title')}</h2>
              <p className="help-text">
                {t('setupPage:telemetry.description')}
              </p>

              <div className="info-card" style={{ background: "#e7f3ff", border: "1px solid #6264a7", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
                <div style={{ color: "#003768", fontSize: "14px" }}>
                  <strong>{t('setupPage:telemetry.info')}</strong>
                </div>
              </div>

              <div className="status-card">
                <div className="status-content">
                  <div className="telemetry-toggle-container">
                    <Toggle
                      checked={telemetryEnabled}
                      onChange={async (event, checked) => {
                        setLoadingTelemetryPref(true);
                        try {
                          // Call API to update telemetry preference
                          const response = await apiService.updateTelemetryPreferences(checked ?? false);

                          if (response.success) {
                            setTelemetryEnabled(checked ?? false);
                            telemetryService.capture('setup_telemetry_toggled', 'setup-page', {
                              properties: { telemetry_enabled: checked ?? false }
                            });
                            console.log(`Telemetry ${checked ? 'enabled' : 'disabled'} successfully`);
                          } else {
                            console.error("Failed to update telemetry preference:", response.error);
                            // Revert the change if API call fails
                            setTelemetryEnabled(!checked);
                            alert(t('setupPage:telemetry.updateError', { error: response.error }));
                          }
                        } catch (error) {
                          console.error("Error updating telemetry preference:", error);
                          setTelemetryEnabled(!checked);
                          alert(t('setupPage:telemetry.updateErrorGeneric'));
                        } finally {
                          setLoadingTelemetryPref(false);
                        }
                      }}
                      disabled={loadingTelemetryPref}
                      label={telemetryEnabled ? t('setupPage:telemetry.enabledLabel') : t('setupPage:telemetry.disabledLabel')}
                      onText={t('setupPage:telemetry.onText')}
                      offText={t('setupPage:telemetry.offText')}
                      styles={{
                        root: {
                          marginBottom: 'var(--spacing-sm)',
                        },
                        label: {
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-text-primary)',
                        },
                        thumb: {
                          backgroundColor: telemetryEnabled ? 'white' : 'var(--color-text-secondary)',
                        }
                      }}
                    />
                    {loadingTelemetryPref && (
                      <div className="telemetry-loading-indicator">
                        <div className="spinner-small"></div>
                        <span>{t('setupPage:telemetry.updating')}</span>
                      </div>
                    )}
                  </div>
                  <p className="telemetry-description">
                    {telemetryEnabled ? t('setupPage:telemetry.enabledDescription') : t('setupPage:telemetry.disabledDescription')}
                  </p>
                </div>
              </div>
            </div>


          </>
        )}
      </div>



      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="notification-modal notification-modal-success">
            <div className="notification-modal-header">
              <div className="notification-modal-title-container">
                <span className="notification-modal-icon">🎉</span>
                <h2 className="notification-modal-title">
                  {t('setupPage:successModal.title')}
                </h2>
              </div>
              <button
                className="notification-modal-close"
                onClick={() => setShowSuccessModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="notification-modal-body">
              <p className="notification-modal-message">
                {t('setupPage:successModal.message')}
              </p>

              {/* Warning if Entra ID Creation Lock is not yet active */}
              {groupCreationSettings?.enableGroupCreation !== false && (
                <div className="setup-modal-warning-card">
                  <div className="setup-modal-warning-content">
                    <div className="setup-modal-warning-header">
                      <span>⚠️</span>
                      <strong>{t('setupPage:successModal.lockWarningTitle')}</strong>
                    </div>
                    <p className="setup-modal-warning-message">
                      {t('setupPage:successModal.lockWarningMessage')}
                    </p>
                    <a
                      href={ENTRA_GROUP_SETTINGS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ display: "inline-block", fontSize: "12px", padding: "6px 12px" }}
                    >
                      🔗 {t('setupPage:successModal.openEntraButton')}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="notification-modal-footer">
              <button
                className="btn btn-primary"
                onClick={onNavigateToAdmin}
              >
                {t('setupPage:successModal.goToAdminButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      {!servicePrincipalMissing && (
        <div className="setup-footer">
          <button className="btn btn-secondary" onClick={refreshPermissions} disabled={loading}>
            {t('setupPage:footer.refreshButton')}
          </button>

          {/* Version Info */}
          <div className="version-info">
            <small>
              {t('setupPage:footer.versionInfo', {
                version: getBuildInfo().fullVersion,
                buildNumber: getBuildInfo().buildNumber,
                buildDate: new Date(getBuildInfo().buildDate).toLocaleString('fr-FR')
              })}
            </small>
          </div>
        </div>
      )}
    </div>
  );
}
